<template>
	<div class="Badge" :class="classes">
		<!-- Icon -->
		<CloIcon v-if="icon" :symbol="icon" />

		<!-- Text -->
		<span class="Badge-label">
			<!-- @slot The label (can be any text) -->
			<slot />
		</span>
	</div>
</template>

<script>
import CloIcon from '@/icons/CloIcon/CloIcon.vue';

export default {
	components: {
		CloIcon,
	},
	data() {
		return {};
	},
	props: {
		/**
		 * The icon to add to the badge
		 */
		icon: {
			type: String,
			default: null,
		},
		/**
		 * The circle styles
		 * TODO: improve this
		 */
		circle: {
			type: String,
			default: null,
		},
		/**
		 * size can be xs, sm, or lg
		 */
		size: {
			type: String,
			default: null,
		},
		/**
		 * state can be new, alert, error, or plain
		 */
		state: {
			type: String,
			default: null,
		},
	},
	computed: {
		classes() {
			const classes = {
				'Badge--tiny': this.size === 'xs',
				'Badge--small': this.size === 'sm',
				'Badge--large': this.size === 'lg',
				'Badge--circle': this.circle,
				'Badge--new': this.state === 'new',
				'Badge--alert': this.state === 'alert',
				'Badge--error': this.state === 'error',
				'Badge--plain': this.state === 'plain',
				'Badge--lightBlue': this.state === 'lightBlue',
				'Badge--mediumBlue': this.state === 'mediumBlue',
				'Badge--circleFilled': this.circle === 'filled',
				'Badge--circledNumber': this.circle === 'number',
			};
			return classes;
		},
	},
};
</script>

<style lang="scss">
/* Parent class */
.Badge {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	vertical-align: middle;
	color: $color-white;
	background-color: $color-black;
	@include text(3);
	font-weight: bold;
	border-radius: 10rem;
	padding-top: 0.1rem;
	padding-bottom: 0.2rem;
	padding-left: 0.6rem;
	padding-right: 0.6rem;
	.Icon {
		@include iconSize(map-get(map-get($fontSizes, 3), size));
		align-self: center;
		&:not(:last-child) {
			@include inline-xs;
		}
	}
}

/* Wrapper for child: Label */
.Badge-label {
	&:not(:last-child) {
		@include inline-xs;
	}
}

/* ------------------
	Variants
-------------------*/

/* Small variant */
.Badge--small {
	@include text(2);
	padding-left: 0.5rem;
	padding-right: 0.5rem;
	.Icon {
		@include iconSize(map-get(map-get($fontSizes, 2), size));
	}
}

/* Tiny variant */
.Badge--tiny {
	@include text(1);
	padding-left: 0.4rem;
	padding-right: 0.4rem;
	.Icon {
		@include iconSize(map-get(map-get($fontSizes, 1), size));
	}
}

/* Large variant */
.Badge--large {
	@include text(4);
	padding: $space-inset-squish-md;
	.Icon {
		@include iconSize(map-get(map-get($fontSizes, 2), size));
	}
}

/* New variant makes badge green */
.Badge--new {
	background-color: $color-secondary-3;
}

/* Alert variant makes badge orange */
.Badge--alert {
	background-color: $color-alert-3;
}

/* Error variant makes badge red */
.Badge--error {
	background-color: $color-error-3;
}

/* Neutral variant makes badge red */
.Badge--neutral {
	background-color: $color-neutral-1;
	color: $color-neutral-4;
}

/* Plain variant removes background color */
.Badge--plain {
	background-color: transparent;
	font-weight: normal;
	color: initial;
	padding: 0;
	&.Badge--neutral {
		color: $color-text-muted;
	}
	&.Badge--new {
		color: $color-link-special;
	}
	&.Badge--alert {
		color: $color-text-alert;
	}
	&.Badge--error {
		color: $color-text-error;
	}
}

/* Circle variant forces badge to a circle */
.Badge--circle {
	background-color: transparent;
	@include text(2);
	text-align: center;
	font-weight: bold;
	min-width: 1.5rem;
	min-height: 1.5rem;
	border-radius: 50%;
	border: 1px solid $color-text-muted;
	padding: 0;
	&.Badge--circleTiny {
		@include text(1);
		min-width: 0.85rem;
		min-height: 0.85rem;
	}
	&.Badge--circledNumber {
		color: $color-text-muted;
	}
	&.Badge--circleFilled {
		color: $color-white;

		&.Badge--circleFilled-new,
		&.Badge--new {
			background-color: $color-secondary-3;
			border-color: $color-secondary-3;
		}
		&.Badge--circleFilled-alert,
		&.Badge--alert {
			background-color: $color-alert-3;
			border-color: $color-alert-3;
		}
		&.Badge--circleFilled-error,
		&.Badge--error {
			background-color: $color-alert-3;
			border-color: $color-alert-3;
		}
		&.Badge--circleFilled-lightBlue,
		&.Badge--lightBlue {
			background-color: $color-primary-2;
			border-color: $color-primary-2;
		}
		&.Badge--circleFilled-mediumBlue,
		&.Badge--mediumBlue {
			background-color: $color-primary-3;
			border-color: $color-primary-3;
		}
	}
}

/* Fixed variant sits at the top line of it's parent container. Centered, then right-aligned for desktop. */
.Badge--fixed {
	position: absolute;
	border-radius: 3px;
	top: 0;
	right: 50%;
	[dir='rtl'] & {
		right: auto !important;
		left: 0;
	}
	transform: translate(50%, -50%);
	&--right {
		right: 0;
		transform: translate($space-sm, -50%);
	}
	&--md-right {
		@include breakpoint(medium) {
			right: 0;
			transform: translate($space-sm, -50%);
		}
	}
	&--lg-right {
		@include breakpoint(large) {
			right: 0;
			transform: translate($space-sm, -50%);
		}
	}
}

// Exotic badges
@mixin badgeExotic {
	background-color: $color-neutral-1;
	color: $color-neutral-4;
	@include text(2);
	line-height: 1;
	text-align: center;
	width: 1.25rem;
	height: 1.25rem;
	border-radius: 50%;
	border: 1px solid $color-neutral-1;
	padding: 0;
}

// Exotic Naturalized
.Badge--exoticNaturalized {
	@include badgeExotic;
}

// Exotic Provisional
.Badge--exoticProvisional {
	@include badgeExotic;
	background-color: $color-alert-1;
	color: $color-alert-4;
	border-color: $color-alert-4;
}

// Exotic Not Established
.Badge--exoticNotEstablished {
	@include badgeExotic;
	background-color: $color-error-3;
	color: $color-white;
	border-color: $color-error-3;
}

// IUCN conservation status badges
.Badge {
	&.u-color-constatus-lc {
		color: $color-text;
		background-color: $color-constatus-lc;
	}
	&.u-color-constatus-nt {
		color: $color-text;
		background-color: $color-constatus-nt;
	}
	&.u-color-constatus-vu {
		color: $color-text;
		background-color: $color-constatus-vu;
	}
	&.u-color-constatus-en {
		color: $color-text;
		background-color: $color-constatus-en;
	}
	&.u-color-constatus-cr {
		background-color: $color-constatus-cr;
	}
	&.u-color-constatus-ex {
		background-color: $color-constatus-ex;
	}
	&.u-color-constatus-ew {
		background-color: $color-constatus-ew;
	}
	&.u-color-constatus-dd {
		background-color: $color-constatus-dd;
	}
	&.u-color-constatus-ne {
		color: $color-text;
		background-color: $color-constatus-ne;
	}
}
</style>
